import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useFreeShippingDisplay from '../../hooks/useFreeShippingDisplay';
import * as types from '../../types';
import useHardwareFirstTotals from './useHardwareFirstTotals';
import styles from './HardwareFirstOrderSummary.module.scss';

interface HardwareFirstOrderSummaryProps {
  onCheckOut: (checkoutCart: types.Cart, cartPricing: types.CartPricing) => void;
}

/**
 * The hardware-first receipt: what the hardware lists at, what came off it, and the pre-tax
 * subtotal, followed by the returns and support reassurances that sit under the button in the
 * design. Tax lands at checkout, so this total is a subtotal rather than a final amount. The
 * only route into checkout is the pinned bar, which carries the same figures at every width.
 */
export default function HardwareFirstOrderSummary({ onCheckOut }: HardwareFirstOrderSummaryProps) {
  const { t } = useTranslation();
  const { isFreeShipping, shippingPrice } = useFreeShippingDisplay();
  const { trackerCount, hardwareInCents, discountInCents, totalInCents, formatPrice } = useHardwareFirstTotals();

  return (
    <div className={styles.summary}>
      <h2 className={styles.title}>{t('hardware_first_cart_order_summary')}</h2>
      <div className={styles.rows}>
        <div className={styles.row}>
          <span className={styles.rowLabel}>
            {trackerCount === 1
              ? t('hardware_first_cart_hardware_one')
              : t('hardware_first_cart_hardware_other', { count: trackerCount })}
          </span>
          <span className={styles.rowValue}>{formatPrice(hardwareInCents)}</span>
        </div>
        {discountInCents > 0 && (
          <div className={classNames(styles.row, styles.discountRow)}>
            <span className={styles.rowLabel}>{t('discount')}</span>
            <span className={styles.rowValue}>{t('negative_amount', { amount: formatPrice(discountInCents) })}</span>
          </div>
        )}
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('hardware_first_cart_ground_shipping')}</span>
          <span className={styles.rowValue}>{isFreeShipping ? t('cart_item_v2.price_free') : shippingPrice}</span>
        </div>
        <div className={styles.divider} />
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>{t('subtotal')}</span>
          <span className={styles.totalValue}>{formatPrice(totalInCents)}</span>
        </div>
        <p className={styles.taxNote}>{t('hardware_first_cart_taxes_at_checkout')}</p>
      </div>
      <div className={styles.reassurance}>
        <span className={styles.reassuranceTitle}>{t('hardware_first_cart_risk_free_title')}</span>
        <span className={styles.reassuranceBody}>{t('hardware_first_cart_risk_free_body')}</span>
      </div>
      <div className={styles.reassurance}>
        <span className={styles.reassuranceTitle}>{t('hardware_first_cart_questions_title')}</span>
        <span className={styles.reassuranceBody}>{t('hardware_first_cart_questions_body')}</span>
      </div>
    </div>
  );
}
